import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { apiUrl } from "../../utils/config";
import { useAppContext } from "../../context";

const AdminContext = createContext();
const API_BASE = apiUrl;

export const AdminProvider = ({ children }) => {
  const { adminUser } = useAppContext();

  // --- STATE ---
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState("home");
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]); // Customers
  const [categories, setCategories] = useState([]);
  const [toast, setToast] = useState(null);
  const [customPages, setCustomPages] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [tables, setTables] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [giftCards, setGiftCards] = useState([]);
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [taxRules, setTaxRules] = useState([]);
  const [returns,  setReturns]  = useState([]);
  const [vendors,  setVendors]  = useState([]);
  const [events,   setEvents]   = useState([]);

  const [adminUsers, setAdminUsers] = useState([
    { id: 1, name: "Admin User", email: "admin@gamapa.com", role: "admin", status: "active", joinDate: "2023-01-15", lastLogin: "2 mins ago" },
    { id: 2, name: "Sarah Manager", email: "sarah@gamapa.com", role: "manager", status: "active", joinDate: "2023-03-10", lastLogin: "1 day ago" },
    { id: 3, name: "Mike Staff", email: "mike@gamapa.com", role: "staff", status: "inactive", joinDate: "2023-06-20", lastLogin: "2 weeks ago" },
  ]);

  const [coupons, setCoupons] = useState([
    { id: 1, code: "WELCOME20", discount: 20, status: "Active" },
    { id: 2, code: "SUMMER50", discount: 50, status: "Expired" },
  ]);
  const { storeSettings, setStoreSettings, loadStoreSettings } = useAppContext();

  // --- Initial Data Fetching ---
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        loadProducts(),
        loadCategories(),
        loadOrders(),
        loadUsers(),
        loadCustomPages(),
        loadBlogPosts(),
        loadRoles(),
        loadMedia(),
        loadContacts(),
        loadTickets(),
        loadTables(),
        loadAppointments(new Date().toISOString().split("T")[0]),
        loadReservations(),
        loadInventory(),
        loadGiftCards(),
        loadDeliveryZones(),
        loadStaff(),
        loadSubscriptionPlans(),
        loadSubscriptions(),
        loadTaxRules(),
        loadReturns(),
        loadVendors(),
        loadEvents(),
      ]);
    };
    loadData();
  }, []);

  // --- ACTIONS ---
  const loadProducts = async () => {
    const prodLocl = await axios.get(`${API_BASE}/menuItem`);
    setProducts([...prodLocl.data.list]);
  };

  // Products
  const deleteProduct = async (id) => {
    const p = products.find(x => x.id === id);
    await axios.delete(`${API_BASE}/menuItem/?id=${id}`);
    logAudit("Deleted", "Product", p?.name || id);
    loadProducts();
  }

  const addProduct = async (product) => {
    await axios.post(`${API_BASE}/menuItem`, product);
    logAudit("Created", "Product", product.name || "New Product");
    loadProducts();
  }

  const updateProduct = async (id, updatedData) => {
    await axios.put(`${API_BASE}/menuItem`, {...updatedData, id});
    logAudit("Updated", "Product", updatedData.name || id);
    loadProducts();
  };

  // ---Categories
  const loadCategories = async () => {
    const catRes = await axios.get(`${API_BASE}/categories`);
    setCategories([...catRes.data.list]);
  };

  const addCategory = async (formData) => {
    await axios.post(`${API_BASE}/categories`, formData);
    logAudit("Created", "Category", formData.name || "New Category");
    loadCategories();
  };

  const updateCategory = async (id, updatedData) => {
    await axios.put(`${API_BASE}/categories`, {...updatedData, id});
    logAudit("Updated", "Category", updatedData.name || id);
    loadCategories();
  };

  const deleteCategory = async (id) => {
    const c = categories.find(x => x.id === id);
    await axios.delete(`${API_BASE}/categories?id=${id}`);
    logAudit("Deleted", "Category", c?.name || id);
    loadCategories();
  }

  // Orders
  const loadOrders = async () => {
    const catRes = await axios.get(`${API_BASE}/bill`);
    setOrders([...catRes.data.list]);
  };


  const updateOrderStatus = async (id, status) => {
    await axios.put(`${API_BASE}/bill`, { id, status });
    logAudit("Changed Status", "Order", `#${id} → ${status}`);
    loadOrders();
  };

  const getOrderById = (id) => orders.find((order) => String(order.id) === String(id));

  // Customers
  const loadUsers = async () => {
    const response = await axios.get(`${API_BASE}/user`);
    setUsers(response.data?.list || []);
  };

  const addUser = async (formData) => {
    await axios.post(`${API_BASE}/user`, formData);
    loadUsers();
  };

  const updateUser = async (id, formData) => {
    await axios.put(`${API_BASE}/user`, { ...formData, id });
    loadUsers();
  };

  const deleteUser = async (id) => {
    await axios.delete(`${API_BASE}/user?id=${id}`);
    loadUsers();
  };

  // Custom Pages — normalize snake_case DB fields to camelCase for the frontend
  const normalizePage = (p) => ({
    ...p,
    navTitle:   p.nav_title   ?? p.navTitle   ?? '',
    showInNav:  p.show_in_nav !== undefined ? Boolean(p.show_in_nav) : (p.showInNav ?? true),
    sections:   Array.isArray(p.sections) ? p.sections
                  : (typeof p.sections === 'string' ? JSON.parse(p.sections || '[]') : []),
  });

  const loadCustomPages = async () => {
    try {
      const res = await axios.get(`${API_BASE}/pages`);
      setCustomPages((res.data?.list || []).map(normalizePage));
    } catch (e) {
      console.error('Failed to load pages', e);
    }
  };

  const saveCustomPage = async (form, editingId) => {
    const payload = {
      title:       form.title,
      slug:        form.slug,
      nav_title:   form.navTitle  || '',
      show_in_nav: form.showInNav ?? true,
      sections:    form.sections  || [],
      status:      form.status    || 'draft',
    };
    if (editingId) {
      await axios.put(`${API_BASE}/pages`, { ...payload, id: editingId });
    } else {
      await axios.post(`${API_BASE}/pages`, payload);
    }
    await loadCustomPages();
  };

  const deleteCustomPage = async (id) => {
    await axios.delete(`${API_BASE}/pages?id=${id}`);
    await loadCustomPages();
  };

  // Blog Posts
  const loadBlogPosts = async () => {
    try {
      const res = await axios.get(`${API_BASE}/blog`);
      setBlogPosts(res.data?.list || []);
    } catch (e) {
      console.error("Failed to load blog posts", e);
    }
  };

  const addBlogPost = async (formData) => {
    await axios.post(`${API_BASE}/blog`, formData);
    logAudit("Created", "Blog Post", formData.title || "New Post");
    await loadBlogPosts();
  };

  const updateBlogPost = async (id, formData) => {
    await axios.put(`${API_BASE}/blog`, { ...formData, id });
    logAudit("Updated", "Blog Post", formData.title || id);
    await loadBlogPosts();
  };

  const deleteBlogPost = async (id) => {
    const p = blogPosts.find(x => x.id === id);
    await axios.delete(`${API_BASE}/blog?id=${id}`);
    logAudit("Deleted", "Blog Post", p?.title || id);
    await loadBlogPosts();
  };

  // Coupons
  const addCoupon = (coupon) => setCoupons(prev => [...prev, { ...coupon, id: Date.now() }]);
  const deleteCoupon = (id) => setCoupons(prev => prev.filter(c => c.id !== id));

  // Roles (API-backed)
  const loadRoles = async () => {
    try {
      const res = await axios.get(`${API_BASE}/userRole`);
      const raw = res.data?.list || [];
      // Parse permissions JSON string from DB if needed
      const parsed = raw.map((r) => ({
        ...r,
        permissions: r.permissions
          ? (typeof r.permissions === 'string' ? JSON.parse(r.permissions) : r.permissions)
          : {},
      }));
      setRoles(parsed);
    } catch (e) {
      console.error('Failed to load roles', e);
    }
  };

  const addRole = async (roleData) => {
    await axios.post(`${API_BASE}/userRole`, { ...roleData, license_id: 2 });
    logAudit("Created", "Role", roleData.name || "New Role");
    await loadRoles();
  };

  const updateRole = async (id, roleData) => {
    await axios.put(`${API_BASE}/userRole`, { ...roleData, id });
    logAudit("Updated", "Role", roleData.name || id);
    await loadRoles();
  };

  const deleteRole = async (id) => {
    const r = roles.find(x => x.id === id);
    await axios.delete(`${API_BASE}/userRole?id=${id}`);
    logAudit("Deleted", "Role", r?.name || id);
    await loadRoles();
  };

  // --- Permission Check ---
  // adminUser.role is now a full role object { id, name, permissions: {...} }
  // coming directly from the backend login response.
  const hasPermission = useCallback((action) => {
    if (!adminUser) return false;

    const perms = adminUser.role?.permissions;
    if (!perms) return false;

    return !!perms[action];
  }, [adminUser]);

  const addAdminUser = (user) => {
    const newUser = {
      id: Date.now(),
      joinDate: new Date().toISOString().split('T')[0],
      lastLogin: "Never",
      ...user
    };
    setAdminUsers(prev => [newUser, ...prev]);
  };

  const updateAdminUser = (id, updatedData) => {
    setAdminUsers(prev => prev.map(u => u.id === id ? { ...u, ...updatedData } : u));
  };

  const deleteAdminUser = (id) => {
    setAdminUsers(prev => prev.filter(u => u.id !== id));
  };

  const saveSettings = async (formData) => {
    try {
      const res = await axios.put(`${API_BASE}/settings`, { ...formData, license_id: 2 });
      if (res.data?.status) {
        logAudit("Updated", "Settings", "Store Settings");
        setStoreSettings(res.data.settings);
        await loadStoreSettings();
        return { status: true };
      }
      return { status: false };
    } catch (e) {
      console.error("Failed to save settings", e);
      return { status: false };
    }
  };

  const showToast = useCallback((msg, type = "success") => {
      setToast({ msg, type });
      setTimeout(() => setToast(null), 3000);
    }, []);

  // --- Audit Log ---
  const logAudit = useCallback((action, entity, target) => {
    axios.post(`${API_BASE}/audit-logs`, {
      user_name:  adminUser?.name  || "Admin",
      user_email: adminUser?.email || "",
      action,
      entity,
      target: String(target || ""),
    }).catch(() => {}); // fire-and-forget; never block the UI
  }, [adminUser]);

  // --- Media Library ---
  const loadMedia = async () => {
    try {
      const res = await axios.get(`${API_BASE}/media`);
      if (res.data.status) setMediaFiles(res.data.files);
    } catch (_) {}
  };

  // folder is a plain string, appended to FormData before posting
  const uploadMedia = async (formData, folder = "general") => {
    formData.append("folder", folder);
    const res = await axios.post(`${API_BASE}/media`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    await loadMedia();
    return res.data;
  };

  const deleteMedia = async (id) => {
    await axios.delete(`${API_BASE}/media/${id}`);
    await loadMedia();
  };

  // --- Contact Submissions ---
  const loadContacts = async () => {
    try {
      const res = await axios.get(`${API_BASE}/contacts`);
      if (res.data.status) setContactSubmissions(res.data.list);
    } catch (_) {}
  };

  const markContactRead = async (id) => {
    await axios.put(`${API_BASE}/contacts/${id}/read`);
    await loadContacts();
  };

  const deleteContact = async (id) => {
    await axios.delete(`${API_BASE}/contacts/${id}`);
    await loadContacts();
  };

  // --- Tickets ---
  const loadTickets = async () => {
    try {
      const res = await axios.get(`${API_BASE}/tickets`);
      if (res.data.status) setTickets(res.data.tickets);
    } catch (_) {}
  };

  const updateTicket = async (id, patch) => {
    await axios.put(`${API_BASE}/tickets/${id}`, patch);
    await loadTickets();
  };

  const addTicketMessage = async (id, payload) => {
    const res = await axios.post(`${API_BASE}/tickets/${id}/messages`, payload);
    await loadTickets();
    return res.data;
  };

  const deleteTicket = async (id) => {
    await axios.delete(`${API_BASE}/tickets/${id}`);
    await loadTickets();
  };

  // --- Tables ---
  const loadTables = async () => {
    try {
      const res = await axios.get(`${API_BASE}/licenseTable`);
      if (res.data.status) setTables(res.data.list);
      else setTables([]);
    } catch (_) {}
  };

  const addTable = async (data) => {
    await axios.post(`${API_BASE}/licenseTable`, data);
    logAudit("Created", "Table", data.name || "New Table");
    await loadTables();
  };

  const updateTable = async (id, data) => {
    await axios.put(`${API_BASE}/licenseTable`, { ...data, id });
    logAudit("Updated", "Table", data.name || id);
    await loadTables();
  };

  const deleteTable = async (id) => {
    const t = tables.find(x => x.id === id);
    await axios.delete(`${API_BASE}/licenseTable?id=${id}`);
    logAudit("Deleted", "Table", t?.name || id);
    await loadTables();
  };

  // --- Appointments ---
  const loadAppointments = async (date) => {
    try {
      const params = date ? `?date=${date}&license_id=2` : "?license_id=2";
      const res = await axios.get(`${API_BASE}/licenseAppointment${params}`);
      if (res.data.status) setAppointments(res.data.list);
      else setAppointments([]);
    } catch (_) {}
  };

  const addAppointment = async (data) => {
    const res = await axios.post(`${API_BASE}/licenseAppointment`, data);
    await loadAppointments();
    return res.data;
  };

  const updateAppointment = async (id, data) => {
    await axios.put(`${API_BASE}/licenseAppointment`, { ...data, id });
    await loadAppointments();
  };

  const deleteAppointment = async (id) => {
    await axios.delete(`${API_BASE}/licenseAppointment?id=${id}`);
    await loadAppointments();
  };

  // --- Reservations ---
  const loadReservations = async ({ date, status } = {}) => {
    try {
      const params = new URLSearchParams({ license_id: 2 });
      if (date)   params.append("date",   date);
      if (status) params.append("status", status);
      const res = await axios.get(`${API_BASE}/reservations?${params}`);
      setReservations(res.data?.list || []);
    } catch (_) {}
  };

  const addReservation = async (data) => {
    await axios.post(`${API_BASE}/reservations`, { ...data, license_id: 2 });
    await loadReservations();
  };

  const updateReservation = async (id, data) => {
    await axios.put(`${API_BASE}/reservations/${id}`, data);
    logAudit("Updated", "Reservation", `#${id}${data.status ? ` → ${data.status}` : ""}`);
    await loadReservations();
  };

  const deleteReservation = async (id) => {
    await axios.delete(`${API_BASE}/reservations/${id}`);
    logAudit("Deleted", "Reservation", `#${id}`);
    await loadReservations();
  };

  // --- Inventory ---
  const loadInventory = async () => {
    try {
      const res = await axios.get(`${API_BASE}/inventory`);
      setInventory(res.data?.list || []);
    } catch (_) {}
  };

  const upsertInventory = async (menu_item_id, data) => {
    await axios.put(`${API_BASE}/inventory/${menu_item_id}`, data);
    logAudit("Updated", "Inventory", data.menu_item_name || `Item #${menu_item_id}`);
    await loadInventory();
  };

  const deleteInventory = async (id) => {
    const item = inventory.find(x => x.id === id);
    await axios.delete(`${API_BASE}/inventory/${id}`);
    logAudit("Deleted", "Inventory", item?.menu_item_name || id);
    await loadInventory();
  };

  // --- Gift Cards ---
  const loadGiftCards = async () => {
    try {
      const res = await axios.get(`${API_BASE}/giftcards`);
      setGiftCards(res.data?.list || []);
    } catch (_) {}
  };

  const createGiftCard = async (data) => {
    await axios.post(`${API_BASE}/giftcards`, data);
    logAudit("Created", "Gift Card", data.code || "New Gift Card");
    await loadGiftCards();
  };

  const cancelGiftCard = async (id) => {
    await axios.put(`${API_BASE}/giftcards/${id}/status`, { status: "cancelled" });
    logAudit("Changed Status", "Gift Card", `#${id} → cancelled`);
    await loadGiftCards();
  };

  const deleteGiftCard = async (id) => {
    await axios.delete(`${API_BASE}/giftcards/${id}`);
    logAudit("Deleted", "Gift Card", `#${id}`);
    await loadGiftCards();
  };

  // --- Delivery Zones ---
  const loadDeliveryZones = async () => {
    try {
      const res = await axios.get(`${API_BASE}/deliveryzones?license_id=2`);
      setDeliveryZones(res.data?.list || []);
    } catch (_) {}
  };

  const createDeliveryZone = async (data) => {
    await axios.post(`${API_BASE}/deliveryzones`, { ...data, license_id: 2 });
    await loadDeliveryZones();
  };

  const updateDeliveryZone = async (id, data) => {
    await axios.put(`${API_BASE}/deliveryzones/${id}`, data);
    await loadDeliveryZones();
  };

  const deleteDeliveryZone = async (id) => {
    await axios.delete(`${API_BASE}/deliveryzones/${id}`);
    await loadDeliveryZones();
  };

  // --- Staff ---
  const loadStaff = async () => {
    try {
      const res = await axios.get(`${API_BASE}/staff?license_id=2`);
      setStaffList(res.data?.list || []);
    } catch (_) {}
  };
  const createStaff = async (data) => {
    await axios.post(`${API_BASE}/staff`, { ...data, license_id: 2 });
    logAudit("Created", "Staff", data.name || "New Staff");
    await loadStaff();
  };
  const updateStaff = async (id, data) => {
    await axios.put(`${API_BASE}/staff/${id}`, data);
    logAudit("Updated", "Staff", data.name || id);
    await loadStaff();
  };
  const deleteStaff = async (id) => {
    const s = staffList.find(x => x.id === id);
    await axios.delete(`${API_BASE}/staff/${id}`);
    logAudit("Deleted", "Staff", s?.name || id);
    await loadStaff();
  };

  // --- Subscriptions ---
  const loadSubscriptionPlans = async () => {
    try {
      const res = await axios.get(`${API_BASE}/subscriptions/plans?license_id=2`);
      setSubscriptionPlans(res.data?.list || []);
    } catch (_) {}
  };
  const createSubscriptionPlan = async (data) => {
    await axios.post(`${API_BASE}/subscriptions/plans`, { ...data, license_id: 2 });
    await loadSubscriptionPlans();
  };
  const updateSubscriptionPlan = async (id, data) => {
    await axios.put(`${API_BASE}/subscriptions/plans/${id}`, data);
    await loadSubscriptionPlans();
  };
  const deleteSubscriptionPlan = async (id) => {
    await axios.delete(`${API_BASE}/subscriptions/plans/${id}`);
    await loadSubscriptionPlans();
  };
  const loadSubscriptions = async () => {
    try {
      const res = await axios.get(`${API_BASE}/subscriptions`);
      setSubscriptions(res.data?.list || []);
    } catch (_) {}
  };
  const updateSubscriptionStatus = async (id, status) => {
    await axios.put(`${API_BASE}/subscriptions/${id}/status`, { status });
    await loadSubscriptions();
  };

  // --- Tax Rules ---
  const loadTaxRules = async () => {
    try {
      const res = await axios.get(`${API_BASE}/taxrules?license_id=2`);
      setTaxRules(res.data?.list || []);
    } catch (_) {}
  };
  const createTaxRule = async (data) => {
    await axios.post(`${API_BASE}/taxrules`, { ...data, license_id: 2 });
    await loadTaxRules();
  };
  const updateTaxRule = async (id, data) => {
    await axios.put(`${API_BASE}/taxrules/${id}`, data);
    await loadTaxRules();
  };
  const deleteTaxRule = async (id) => {
    await axios.delete(`${API_BASE}/taxrules/${id}`);
    await loadTaxRules();
  };

  // --- Returns ---
  const loadReturns = async () => {
    try {
      const res = await axios.get(`${API_BASE}/returns?license_id=2`);
      setReturns(res.data?.list || []);
    } catch (_) {}
  };
  const createReturn = async (data) => {
    await axios.post(`${API_BASE}/returns`, { ...data, license_id: 2 });
    logAudit("Created", "Return", `Order #${data.order_id}`);
    await loadReturns();
  };
  const updateReturn = async (id, data) => {
    await axios.put(`${API_BASE}/returns/${id}`, data);
    logAudit("Updated", "Return", `#${id}${data.status ? ` → ${data.status}` : ""}`);
    await loadReturns();
  };
  const deleteReturn = async (id) => {
    await axios.delete(`${API_BASE}/returns/${id}`);
    logAudit("Deleted", "Return", `#${id}`);
    await loadReturns();
  };

  // --- Vendors ---
  const loadVendors = async () => {
    try {
      const res = await axios.get(`${API_BASE}/vendors?license_id=2`);
      setVendors(res.data?.list || []);
    } catch (_) {}
  };
  const createVendor = async (data) => {
    await axios.post(`${API_BASE}/vendors`, { ...data, license_id: 2 });
    logAudit("Created", "Vendor", data.name || "New Vendor");
    await loadVendors();
  };
  const updateVendor = async (id, data) => {
    await axios.put(`${API_BASE}/vendors/${id}`, data);
    logAudit("Updated", "Vendor", data.name || id);
    await loadVendors();
  };
  const deleteVendor = async (id) => {
    const v = vendors.find(x => x.id === id);
    await axios.delete(`${API_BASE}/vendors/${id}`);
    logAudit("Deleted", "Vendor", v?.name || id);
    await loadVendors();
  };

  // --- Events ---
  const loadEvents = async () => {
    try {
      const res = await axios.get(`${API_BASE}/events?license_id=2`);
      setEvents(res.data?.list || []);
    } catch (_) {}
  };
  const createEvent = async (data) => {
    await axios.post(`${API_BASE}/events`, { ...data, license_id: 2 });
    logAudit("Created", "Event", data.title || "New Event");
    await loadEvents();
  };
  const updateEvent = async (id, data) => {
    await axios.put(`${API_BASE}/events/${id}`, data);
    logAudit("Updated", "Event", data.title || id);
    await loadEvents();
  };
  const deleteEvent = async (id) => {
    const ev = events.find(x => x.id === id);
    await axios.delete(`${API_BASE}/events/${id}`);
    logAudit("Deleted", "Event", ev?.title || id);
    await loadEvents();
  };

  // --- Loyalty (virtual cards + points) ---
  const getUserLoyalty = async (user_id) => {
    try {
      const res = await axios.get(`${API_BASE}/loyalty?user_id=${user_id}`);
      return res.data?.status ? res.data : null;
    } catch (_) {
      return null;
    }
  };

  const awardPoints = async (user_id, points, description) => {
    const res = await axios.post(`${API_BASE}/loyalty/points/award`, { user_id, points, description });
    return res.data;
  };

  const updateCardStatus = async (card_id, status) => {
    const res = await axios.put(`${API_BASE}/loyalty/card/${card_id}`, { status });
    return res.data;
  };

  return (
    <AdminContext.Provider value={{
      products, deleteProduct, addProduct, updateProduct,
      orders, updateOrderStatus, getOrderById, loadOrders,
      users, addUser, updateUser, deleteUser, loadUsers,
      coupons, addCoupon, deleteCoupon,
      categories, addCategory, updateCategory, deleteCategory,
      roles, loadRoles, addRole, updateRole, deleteRole,
      adminUsers, addAdminUser, updateAdminUser, deleteAdminUser,
      customPages, loadCustomPages, saveCustomPage, deleteCustomPage,
      blogPosts, loadBlogPosts, addBlogPost, updateBlogPost, deleteBlogPost,
      toast, setToast, showToast,
      page, setPage,
      hasPermission,
      storeSettings, saveSettings,
      mediaFiles, loadMedia, uploadMedia, deleteMedia,
      contactSubmissions, loadContacts, markContactRead, deleteContact,
      tickets, loadTickets, updateTicket, addTicketMessage, deleteTicket,
      tables, loadTables, addTable, updateTable, deleteTable,
      appointments, loadAppointments, addAppointment, updateAppointment, deleteAppointment,
      reservations, loadReservations, addReservation, updateReservation, deleteReservation,
      getUserLoyalty, awardPoints, updateCardStatus,
      inventory, loadInventory, upsertInventory, deleteInventory,
      giftCards, loadGiftCards, createGiftCard, cancelGiftCard, deleteGiftCard,
      deliveryZones, loadDeliveryZones, createDeliveryZone, updateDeliveryZone, deleteDeliveryZone,
      staffList, loadStaff, createStaff, updateStaff, deleteStaff,
      subscriptionPlans, loadSubscriptionPlans, createSubscriptionPlan, updateSubscriptionPlan, deleteSubscriptionPlan,
      subscriptions, loadSubscriptions, updateSubscriptionStatus,
      taxRules, loadTaxRules, createTaxRule, updateTaxRule, deleteTaxRule,
      returns,  loadReturns,  createReturn,  updateReturn,  deleteReturn,
      vendors,  loadVendors,  createVendor,  updateVendor,  deleteVendor,
      events,   loadEvents,   createEvent,   updateEvent,   deleteEvent,
      logAudit,
    }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => useContext(AdminContext);
